import { Box, Button, Flex, FormControl, FormLabel, HStack, SimpleGrid, Spinner, Text, useColorModeValue, useToast } from "@chakra-ui/react";
import { useCallback, useEffect, useState } from "react";
import FormField from "../../../components/fields/FormField";
import ErrorState from '../../../components/states/ErrorState';
import PageCard from "../../../components/page-card";
import { translate } from "../../../i18n";
import { typography } from 'theme/tokens';
import { paramFromWindowLocation } from "../../../utils/url-parser";
import SportSelect from "components/SportSelect";
import LocationImageUploader from "../components/LocationImageUploader";
import { useGetLocation } from "../hooks/useGetLocation";
import { useUpdateLocation } from "../hooks/useUpdateLocation";

export default function LocationPage() {
    const locationId = paramFromWindowLocation('location')

    const { data: location, isLoading, isError } = useGetLocation(locationId);
    const [hasBeenEdited, setHasBeenEdited] = useState(false);
    const toast = useToast();
    const labelColor = useColorModeValue('secondaryGray.900', 'white');
    const { mutateAsync: updateLocation, isLoading: isUpdatingLocation } = useUpdateLocation(locationId, () => {
        toast({
            title: translate('locations.location_edited_successfully'),
            status: 'success',
            duration: 3000,
            position: 'top-right'
        });
        setHasBeenEdited(false);
    });
    const [editedLocation, setEditedLocation] = useState(() => {
        if (!location)
            return null;
        return { ...location };
    });

    useEffect(() => {
        if (location && !editedLocation)
            setEditedLocation({ ...location });
    }, [location, editedLocation]);


    const onValueChange = useCallback((key: keyof typeof editedLocation, value: any) => {
        setEditedLocation(prev => {
            return { ...prev, [key]: value };
        });
        setHasBeenEdited(true);
    }, [setEditedLocation]);

    // `#/admin/locationDetails` with no `?location=` is a reachable URL (a
    // bookmark, a shared link with the query dropped, a hand-typed path). The
    // query is gated off in that state, and a gated-off react-query v4 query
    // reports `isLoading` forever — so without this branch the page would spin
    // with nothing in flight. Say what is wrong instead.
    if (!locationId) {
        return <ErrorState message={translate('locations.error_loading')} />;
    }

    if (isLoading) {
        return (
            <Flex justify="center" align="center" minH="200px">
                <Spinner size="xl" />
            </Flex>
        );
    }

    if (isError) {
        return <ErrorState message={translate('locations.error_loading')} />;
    }

    return (
        <PageCard
            showBackButton
            title={location?.name}
            rightHeader={
                <HStack>
                    <Button
                        isDisabled={!hasBeenEdited}
                        isLoading={isUpdatingLocation}
                        colorScheme="brand"
                        onClick={async () => {
                            updateLocation({
                                ...editedLocation,
                                state: editedLocation.state || ' ',
                                address: editedLocation.address || ' ',
                                city: editedLocation.city || ' ',
                                postalCode: editedLocation.postalCode || ' ',
                                country: editedLocation.country || ' ',
                                googleMapsLink: editedLocation.googleMapsLink || ' ',
                                name: editedLocation.name || ' ',
                                sportTypes: editedLocation.sports
                            }).catch(() => {
                                toast({
                                    title: translate('common.error'),
                                    status: 'error',
                                    duration: 3000,
                                    position: 'top-right'
                                });
                            });
                        }}
                    >
                        {translate('save')}
                    </Button>
                </HStack>
            }
        >

            <SimpleGrid columns={{ sm: 2, }}>
                <Box px={6} pb={6}>
                    {locationId && (
                        <LocationImageUploader
                            locationId={locationId}
                            imageUrl={location?.imageUrl}
                        />
                    )}
                </Box>
                <Box px={6} pb={6}>
                    <FormField label={translate('locations.name')} value={editedLocation?.name || ''} onChange={(v: string) => onValueChange('name', v)} />

                    <FormControl><FormLabel {...typography.formLabel} color={labelColor} mb="6px">{translate('locations.sports')}</FormLabel>
                        <SportSelect
                            value={editedLocation?.sports || []}
                            onChange={(newValue) => onValueChange('sports', newValue)}
                        />
                    </FormControl>

                    <FormField label={translate('locations.state')} value={editedLocation?.state || ''} onChange={(v: string) => onValueChange('state', v)} />

                    <FormField label={translate('locations.address')} value={editedLocation?.address || ''} onChange={(v: string) => onValueChange('address', v)} />

                    <FormField label={translate('locations.city')} value={editedLocation?.city || ''} onChange={(v: string) => onValueChange('city', v)} />

                    <FormField label={translate('locations.postal_code')} value={editedLocation?.postalCode || ''} onChange={(v: string) => onValueChange('postalCode', v)} />

                    <FormField label={translate('locations.country')} value={editedLocation?.country || ''} onChange={(v: string) => onValueChange('country', v)} />

                    <FormField label={translate('locations.googleMapsLink')} value={editedLocation?.googleMapsLink || ''} onChange={(v: string) => onValueChange('googleMapsLink', v)} />
                </Box>
            </SimpleGrid>
        </PageCard>
    )
}
